import { Component, OnInit, inject, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { toast } from 'ngx-sonner';
import { filter, take } from 'rxjs';

import { RadioButtonModule } from 'primeng/radiobutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

import { environment } from '../../../../../environments/environment';
import { selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { BookingSettingsService } from '../../../../core/services/booking-settings.service';
import {
  PublicBookingLinkService,
  PublicBookingLinkRow,
} from '../../../../core/services/public-booking-link.service';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';

interface DoctorOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-booking-settings-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    RadioButtonModule,
    MultiSelectModule,
    InputTextModule,
    SelectModule,
    ConfirmDialogModule,
    AppButton,
    HasPermissionDirective,
  ],
  templateUrl: './booking-settings-tab.html',
  providers: [ConfirmationService],
})
export class BookingSettingsTab implements OnInit {
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private bookingSettingsService = inject(BookingSettingsService);
  private publicBookingLinkService = inject(PublicBookingLinkService);
  private professionalsService = inject(ProfessionalsService);
  private translate = inject(TranslateService);
  private confirmationService = inject(ConfirmationService);
  private cdr = inject(ChangeDetectorRef);
  private perm = inject(PermissionService);

  get canEdit(): boolean {
    return this.perm.canEdit(MODULE.CONFIGURACOES);
  }

  bookingForm!: FormGroup;

  bookingModeOptions = [
    { label: 'booking_settings_mode_auto_confirm', value: 'auto_confirm' },
    { label: 'booking_settings_mode_requires_approval', value: 'requires_approval' },
  ];

  doctorSelectionOptions = [
    { label: 'booking_settings_doctor_selection_all', value: 'all' },
    { label: 'booking_settings_doctor_selection_selected', value: 'selected' },
  ];

  bookingWindowPresets = [7, 30, 90, 180];
  rescheduleCutoffPresets = [24, 48, 72];

  isLoading = signal(true);
  isSaving = signal(false);
  doctorOptions = signal<DoctorOption[]>([]);

  // ── Public booking links ───────────────────────────────────────────────────
  /** Mint form, kept separate from bookingForm: these write through an RPC, not the settings row. */
  createLinkForm!: FormGroup;
  links = signal<PublicBookingLinkRow[]>([]);
  isLoadingLinks = signal(true);
  isCreatingLink = signal(false);
  showCreateLink = signal(false);
  /** Per-row busy flags — the list stays usable while one row is mid-flight. */
  rotatingId = signal<number | null>(null);
  deactivatingId = signal<number | null>(null);
  /** QR codes render on demand; 50 inline <img> fetches on load would be absurd. */
  expandedQrId = signal<number | null>(null);

  /**
   * The CRM app's origin, trailing slashes stripped.
   *
   * NOT window.location.origin: that is this clinic app, and /book-open is a
   * route of the CRM app. Blank when unconfigured — see crmAppUrl in
   * environment.ts, and the warning banner the template shows in that case.
   */
  readonly crmAppUrl = (environment.crmAppUrl ?? '').trim().replace(/\/+$/, '');

  get isCrmAppUrlConfigured(): boolean {
    return this.crmAppUrl.length > 0;
  }

  /** Server-side cap is 50 ACTIVE links per branch; shown so the ceiling is not a surprise. */
  readonly maxActiveLinks = 50;

  activeLinkCount(): number {
    return this.links().filter((l) => l.is_active).length;
  }

  private branchId: number | null = null;

  ngOnInit() {
    this.initForm();
    this.store
      .select(selectBranchId)
      .pipe(
        filter((id) => !!id),
        take(1)
      )
      .subscribe((branchId) => {
        this.branchId = branchId;
        this.loadDoctors(branchId);
        this.loadBookingSettings(branchId);
        this.loadLinks(branchId);
      });
  }

  private initForm() {
    this.bookingForm = this.fb.group({
      booking_mode: ['auto_confirm', Validators.required],
      doctor_selection_mode: ['all', Validators.required],
      doctor_ids: [[] as number[]],
      booking_window_days: [90, [Validators.required, Validators.min(1), Validators.max(365)]],
      reschedule_cutoff_hours: [24, [Validators.required, Validators.min(0), Validators.max(720)]],
    });

    // Both optional: an unlabelled link on no particular doctor is a perfectly
    // good "put this QR on the counter" link. maxLength mirrors the table's
    // own 120-char check constraint so the error surfaces before the round-trip.
    this.createLinkForm = this.fb.group({
      label: ['', Validators.maxLength(120)],
      doctor_id: [null as number | null],
    });

    // doctor_ids is only meaningful (and required) when doctor_selection_mode is
    // 'selected' — mirrors the type/professional-or-room toggle in
    // block-schedule-modal.component.ts.
    this.bookingForm.get('doctor_selection_mode')?.valueChanges.subscribe((mode) => {
      const doctorIdsControl = this.bookingForm.get('doctor_ids');
      if (mode === 'selected') {
        doctorIdsControl?.setValidators(Validators.required);
      } else {
        doctorIdsControl?.clearValidators();
        doctorIdsControl?.setValue([]);
      }
      doctorIdsControl?.updateValueAndValidity();
    });
  }

  private loadDoctors(branchId: number) {
    this.professionalsService.searchDoctors(branchId, '', 200, 0).then(({ data, error }) => {
      if (error) {
        console.error('Error loading doctors:', error);
        return;
      }
      this.doctorOptions.set((data as DoctorOption[]) || []);
      this.cdr.detectChanges();
    });
  }

  private loadBookingSettings(branchId: number) {
    this.isLoading.set(true);
    this.bookingSettingsService.getBookingSettings(branchId).subscribe({
      next: (settings) => {
        this.bookingForm.patchValue(settings);
        if (settings.doctor_selection_mode === 'selected') {
          this.bookingForm.get('doctor_ids')?.setValidators(Validators.required);
          this.bookingForm.get('doctor_ids')?.updateValueAndValidity();
        }
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading booking settings:', err);
        this.isLoading.set(false);
        toast.error(this.translate.instant('booking_settings_error_load'));
        this.cdr.detectChanges();
      },
    });
  }

  applyWindowPreset(days: number) {
    if (!this.canEdit) return;
    this.bookingForm.get('booking_window_days')?.setValue(days);
  }

  applyCutoffPreset(hours: number) {
    if (!this.canEdit) return;
    this.bookingForm.get('reschedule_cutoff_hours')?.setValue(hours);
  }

  onSubmit() {
    if (!this.canEdit || !this.branchId) return;
    if (this.bookingForm.invalid) {
      this.bookingForm.markAllAsTouched();
      toast.error(this.translate.instant('common_fix_errors'));
      return;
    }

    this.isSaving.set(true);
    this.bookingSettingsService.saveBookingSettings(this.branchId, this.bookingForm.value).subscribe({
      next: () => {
        this.isSaving.set(false);
        toast.success(this.translate.instant('booking_settings_success_save'));
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error saving booking settings:', err);
        this.isSaving.set(false);
        toast.error(this.translate.instant('booking_settings_error_save'));
        this.cdr.detectChanges();
      },
    });
  }

  // ── Public booking links ───────────────────────────────────────────────────

  private loadLinks(branchId: number) {
    this.isLoadingLinks.set(true);
    this.publicBookingLinkService.getLinks(branchId).subscribe({
      next: (rows) => {
        this.links.set(rows);
        this.isLoadingLinks.set(false);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading public booking links:', err);
        this.isLoadingLinks.set(false);
        toast.error(this.translate.instant('public_booking_link_error_load'));
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * The full public URL for a link.
   *
   * Returns the bare PATH when crmAppUrl is unconfigured, so staff can still see
   * and read the slug — the template disables copy/QR in that state rather than
   * handing out a half-formed address that looks complete.
   */
  linkUrl(link: PublicBookingLinkRow): string {
    const path = `/book-open?l=${encodeURIComponent(link.slug)}`;
    return this.isCrmAppUrlConfigured ? `${this.crmAppUrl}${path}` : path;
  }

  /**
   * QR image for a link, via the same api.qrserver.com service the clinic tab's
   * patient-registration QR codes already use (see clinic-tab.ts) — the repo has
   * no QR library and this adds no dependency.
   */
  qrUrl(link: PublicBookingLinkRow): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
      this.linkUrl(link)
    )}`;
  }

  toggleQr(link: PublicBookingLinkRow) {
    this.expandedQrId.set(this.expandedQrId() === link.id ? null : link.id);
  }

  /** Resolved from the list the tab already loads; a doctor since removed falls back to the raw id. */
  doctorName(doctorId: number | null): string {
    if (doctorId == null) return this.translate.instant('public_booking_link_any_doctor');
    const match = this.doctorOptions().find((d) => d.id === doctorId);
    return match ? match.name : `#${doctorId}`;
  }

  copyLink(link: PublicBookingLinkRow) {
    if (!this.isCrmAppUrlConfigured) return;
    navigator.clipboard
      .writeText(this.linkUrl(link))
      .then(() => toast.success(this.translate.instant('link_copied')))
      .catch(() => toast.error(this.translate.instant('public_booking_link_error_copy')));
  }

  toggleCreateLink() {
    if (!this.canEdit) return;
    this.showCreateLink.set(!this.showCreateLink());
    if (!this.showCreateLink()) this.createLinkForm.reset({ label: '', doctor_id: null });
  }

  createLink() {
    if (!this.canEdit || !this.branchId) return;
    if (this.createLinkForm.invalid) {
      this.createLinkForm.markAllAsTouched();
      toast.error(this.translate.instant('common_fix_errors'));
      return;
    }

    const label = ((this.createLinkForm.value.label as string) || '').trim();
    const doctorId = (this.createLinkForm.value.doctor_id as number | null) ?? null;

    this.isCreatingLink.set(true);
    this.publicBookingLinkService.createLink(this.branchId, doctorId, label || null).subscribe({
      next: (result) => {
        this.isCreatingLink.set(false);
        // Every failure here is a FLAGGED FIELD on a successful RPC call
        // (`ok:false, reason:...`), not a thrown error — same convention as
        // approve_booking_request. Do not move this into the error handler.
        if (!result?.ok) {
          toast.error(this.createLinkErrorMessage(result?.reason));
          this.cdr.detectChanges();
          return;
        }
        toast.success(this.translate.instant('public_booking_link_success_create'));
        this.showCreateLink.set(false);
        this.createLinkForm.reset({ label: '', doctor_id: null });
        // Reload rather than push the returned {id, slug}: the row carries
        // server-set defaults (created_at, utm_*) the RPC does not echo back.
        if (this.branchId) this.loadLinks(this.branchId);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error creating public booking link:', err);
        this.isCreatingLink.set(false);
        toast.error(this.translate.instant('public_booking_link_error_create'));
        this.cdr.detectChanges();
      },
    });
  }

  private createLinkErrorMessage(reason?: string): string {
    switch (reason) {
      case 'forbidden':
        return this.translate.instant('public_booking_link_error_forbidden');
      // Validated against the PUBLICLY BOOKABLE doctors, not the branch's full
      // list: a doctor excluded by the allowlist above, or with no availability,
      // is rejected here precisely so a printed QR never opens an empty grid.
      case 'unknown_doctor':
        return this.translate.instant('public_booking_link_error_unknown_doctor');
      case 'unknown_campaign':
      case 'unknown_qr':
        return this.translate.instant('public_booking_link_error_unknown_ref');
      case 'too_many_links':
        return this.translate.instant('public_booking_link_error_too_many');
      default:
        return this.translate.instant('public_booking_link_error_create');
    }
  }

  confirmRotate(link: PublicBookingLinkRow) {
    if (!this.canEdit) return;
    this.confirmationService.confirm({
      key: 'publicBookingLinkConfirm',
      message: this.translate.instant('public_booking_link_rotate_confirm_message'),
      header: this.translate.instant('public_booking_link_rotate_confirm_title'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      accept: () => this.rotateLink(link),
    });
  }

  private rotateLink(link: PublicBookingLinkRow) {
    this.rotatingId.set(link.id);
    this.publicBookingLinkService.rotateLink(link.id).subscribe({
      next: (result) => {
        this.rotatingId.set(null);
        if (!result?.ok) {
          // too_many_links is reachable here as well as on create: replacing
          // now ADDS an active link rather than retiring the old one (that is
          // the whole point — a printed QR keeps working until the reprint is
          // out), so it counts against the same 50-active cap.
          const key =
            result?.reason === 'forbidden'
              ? 'public_booking_link_error_forbidden'
              : result?.reason === 'too_many_links'
                ? 'public_booking_link_error_too_many'
                : 'public_booking_link_error_rotate';
          toast.error(this.translate.instant(key));
          this.cdr.detectChanges();
          return;
        }
        toast.success(this.translate.instant('public_booking_link_success_rotate'));
        if (this.branchId) this.loadLinks(this.branchId);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error rotating public booking link:', err);
        this.rotatingId.set(null);
        toast.error(this.translate.instant('public_booking_link_error_rotate'));
        this.cdr.detectChanges();
      },
    });
  }

  confirmDeactivate(link: PublicBookingLinkRow) {
    if (!this.canEdit) return;
    this.confirmationService.confirm({
      key: 'publicBookingLinkConfirm',
      message: this.translate.instant('public_booking_link_deactivate_confirm_message'),
      header: this.translate.instant('public_booking_link_deactivate_confirm_title'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      accept: () => this.deactivateLink(link),
    });
  }

  private deactivateLink(link: PublicBookingLinkRow) {
    this.deactivatingId.set(link.id);
    this.publicBookingLinkService.deactivateLink(link.id).subscribe({
      next: () => {
        this.deactivatingId.set(null);
        toast.success(this.translate.instant('public_booking_link_success_deactivate'));
        if (this.branchId) this.loadLinks(this.branchId);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error deactivating public booking link:', err);
        this.deactivatingId.set(null);
        toast.error(this.translate.instant('public_booking_link_error_deactivate'));
        this.cdr.detectChanges();
      },
    });
  }
}
